import { useState } from "react";
import useSWRMutation from "swr/mutation";
import { BiCaretDown } from "react-icons/bi";
import { FiLoader, FiRefreshCw } from "react-icons/fi";

import { OutlineButton, PrimaryButton } from "../../../components/Button";
import useMessageModal from "../../../hooks/useMessageModal.hook";
import { sendPostRequest } from "../../../config/swr";

/**
 * MandateAuthorization renders a collapsible section to:
 * - Fetch mandate details for a loan
 * - Initiate mandate authorization for a loan
 *
 * Backend endpoints (from spec):
 * GET  /loan/{loanId}/mandate               // returns GetMandateData directly
 * POST /loan/{loanId}/initiate-mandate      // returns { ok, message, data }
 * Request payload for both: { customerId, loanId }
 */
const MandateAuthorization = ({ loan }) => {
  const showMessageModal = useMessageModal();

  const [opened, setOpened] = useState(false);
  const [mandate, setMandate] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [initiating, setInitiating] = useState(false);
  const [provider, setProvider] = useState("recova");

  // GET mandate details using the global SWR fetcher (axios GET)
  const { trigger: getMandateTrigger } = useSWRMutation(
    `/loan/${loan.loanID}/mandate?customerId=${loan.customerID}`
  );

  // POST initiate mandate using helper
  const { trigger: initiateMandateTrigger } = useSWRMutation(
    `/loan/${loan.loanID}/initiate-mandate`,
    sendPostRequest
  );

  const toggleOpened = async () => {
    setOpened(!opened);
    // On first open, try to fetch mandate details
    if (!opened && !mandate) await fetchMandateDetails();
  };

  const fetchMandateDetails = async () => {
    try {
      setFetching(true);
      const data = await getMandateTrigger(); // default fetcher returns data
      setMandate(data || null);
    } catch (error) {
      showMessageModal({
        title: "Error",
        message:
          error?.response?.data?.errorMessage ||
          error?.response?.data ||
          "Could not fetch mandate details.",
        isError: true,
        closeButtonText: "Close",
      });
    } finally {
      setFetching(false);
    }
  };

  const initiateMandate = async () => {
    try {
      setInitiating(true);
      const res = await initiateMandateTrigger({
        customerId: loan.customerID,
        loanId: loan.loanID,
        provider,
      });

      showMessageModal({
        title: "Success",
        message: res?.data?.message || "Mandate initiated successfully",
        isError: false,
        closeButtonText: "Close",
      });
      // After initiating, refresh mandate info
      await fetchMandateDetails();
    } catch (error) {
      showMessageModal({
        title: "Error",
        message:
          error?.response?.data?.errorMessage ||
          error?.response?.data ||
          "Could not initiate mandate.",
        isError: true,
        closeButtonText: "Close",
      });
    } finally {
      setInitiating(false);
    }
  };

  const renderValue = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    if (typeof val === "boolean") return val ? "Yes" : "No";
    return val;
  };

  const institution = mandate?.institution || {};
  const statusValue = mandate?.status || mandate?.requestStatus;
  const awaitingConfirmation =
    String(statusValue || "")
      .toLowerCase()
      .replace(/[\s_-]/g, "") === "awaitingconfirmation";

  return (
    <div className="card__box mb-20">
      <div className="flex gap between-center" onClick={toggleOpened}>
        <span>Authorize Mandate</span>
        <span>
          <BiCaretDown
            size={18}
            className={opened ? "dropdown-icon rotate-180" : "dropdown-icon"}
          />
        </span>
      </div>

      <div className={opened ? "message__dropdown opened" : "message__dropdown"}>
        <div className="flex gap-8 mb-12" style={{ alignItems: "center", flexWrap: "wrap" }}>
          <select
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            style={{ minWidth: 140, height: 32, borderRadius: 6, border: "1px solid #d9d9d9", padding: "0 8px" }}
          >
            <option value="recova">Recova</option>
            <option value="mono">Mono (legacy)</option>
          </select>
          <OutlineButton mini onClick={fetchMandateDetails} disabled={fetching}>
            {fetching ? <FiLoader /> : "Refresh Mandate"}
          </OutlineButton>
          <PrimaryButton mini onClick={initiateMandate} disabled={initiating}>
            {initiating ? <FiLoader /> : "Initiate Mandate"}
          </PrimaryButton>
        </div>

        {!mandate ? (
          <p className="fs-16 charcoal-60">No mandate details yet.</p>
        ) : (
          <div className="card__grid">
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">Status:</span>
              <div className="flex gap-8" style={{ alignItems: "center" }}>
                <h3>{renderValue(statusValue)}</h3>
                {awaitingConfirmation && (
                  <button
                    type="button"
                    onClick={fetchMandateDetails}
                    disabled={fetching}
                    title="Refresh mandate status"
                    aria-label="Refresh mandate status"
                    style={{
                      alignItems: "center",
                      border: "1px solid #d9d9d9",
                      borderRadius: 999,
                      color: "#444",
                      cursor: fetching ? "not-allowed" : "pointer",
                      display: "inline-flex",
                      height: 26,
                      justifyContent: "center",
                      opacity: fetching ? 0.65 : 1,
                      width: 26,
                    }}
                  >
                    {fetching ? <FiLoader size={14} /> : <FiRefreshCw size={14} />}
                  </button>
                )}
              </div>
            </div>
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">Reference:</span>
              <h3>{renderValue(mandate.reference || mandate.loanReference)}</h3>
            </div>
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">Amount:</span>
              <h3>{renderValue(mandate.amount || mandate.loanAmount)}</h3>
            </div>
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">Balance:</span>
              <h3>{renderValue(mandate.balance)}</h3>
            </div>
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">Mandate Type:</span>
              <h3>{renderValue(mandate.mandate_type || mandate.mandateType || mandate.repaymentType)}</h3>
            </div>
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">Debit Type:</span>
              <h3>{renderValue(mandate.debit_type || mandate.debitType)}</h3>
            </div>
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">Account Name:</span>
              <h3>{renderValue(mandate.account_name || mandate.accountName)}</h3>
            </div>
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">Account Number:</span>
              <h3>{renderValue(mandate.account_number || mandate.accountNumber || mandate.preferredRepaymentAccount || mandate.linkedAccountNumber)}</h3>
            </div>
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">Live Mode:</span>
              <h3>{renderValue(mandate.live_mode || mandate.liveMode)}</h3>
            </div>
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">Approved:</span>
              <h3>{renderValue(mandate.approved)}</h3>
            </div>
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">Ready To Debit:</span>
              <h3>{renderValue(mandate.ready_to_debit || mandate.readyToDebit)}</h3>
            </div>
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">NIBSS Code:</span>
              <h3>{renderValue(mandate.nibss_code || mandate.NIBSSCode)}</h3>
            </div>
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">Institution:</span>
              <h3>
                {renderValue(
                  institution?.name
                    ? `${institution?.name} (${institution?.bank_code || institution?.bankCode})`
                    : "-"
                )}
              </h3>
            </div>
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">Narration:</span>
              <h3>{renderValue(mandate.narration)}</h3>
            </div>
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">Authorization URL:</span>
              <h3>
                {mandate.redirect_url || mandate.redirectURL || mandate.consentApprovalUrl || mandate.consentConfirmationUrl ? (
                  <a href={mandate.redirect_url || mandate.redirectURL || mandate.consentApprovalUrl || mandate.consentConfirmationUrl} target="_blank" rel="noreferrer">
                    {mandate.redirect_url || mandate.redirectURL || mandate.consentApprovalUrl || mandate.consentConfirmationUrl}
                  </a>
                ) : (
                  renderValue(mandate.redirect_url || mandate.redirectURL || mandate.consentApprovalUrl || mandate.consentConfirmationUrl)
                )}
              </h3>
            </div>
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">Start Date:</span>
              <h3>{renderValue(mandate.start_date || mandate.startDate)}</h3>
            </div>
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">End Date:</span>
              <h3>{renderValue(mandate.end_date || mandate.endDate)}</h3>
            </div>
            <div className="flex gap-8 between-center">
              <span className="charcoal-60">Date:</span>
              <h3>{renderValue(mandate.date)}</h3>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MandateAuthorization;
